import "dayjs/locale/fr";
import DateTimePickerRoot, {
    CalendarComponents,
    useDefaultClassNames,
    useDefaultStyles,
} from "react-native-ui-datepicker";
import { DeviceType, deviceType } from "expo-device";
import { DatePickerProps } from "./DatePicker";
import { useTheme } from "../../hooks/use-theme";
import dayjs from "dayjs";
import { useI18n } from "../../hooks/i18n-hooks";
import Icon from "../Icon";
import { View } from "react-native";
import { useEffect, useMemo, useRef } from "react";
import { toISODateString } from "../../hooks/date";

export const hasNativeDatePicker = (() => {
    if (deviceType === DeviceType.DESKTOP) {
        // We never use desktop's native date pickers as they are either incomplete, buggy, or ugly.
        return false;
    }

    const input = document.createElement("input");
    input.type = "date";

    // Check if the browser recognizes `type="date"`
    const supportsDateInput = input.type === "date";

    // Check if the `showPicker` method exists
    const supportsShowPicker = typeof input.showPicker === "function";

    input.remove();

    return supportsDateInput && supportsShowPicker;
})();

const customCalendarComponents: CalendarComponents = {
    IconPrev: <Icon name="back" />,
    IconNext: <Icon name="arrow-right" />,
};

export const DatePicker = ({
    date,
    minimumDate,
    maximumDate,
    onConfirm,
    isVisible,
    onHide,
    testID,
}: DatePickerProps) => {
    const defaultClassNames = useDefaultClassNames();
    const defaultStyles = useDefaultStyles();
    const { colors } = useTheme();
    const i18n = useI18n();

    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isVisible) {
            inputRef.current?.showPicker?.();
        }
    }, [isVisible]);

    const { normalStyle, activeStyle, activeLabelStyle } = useMemo(() => {
        return {
            normalStyle: {
                color: colors.body,
            },
            activeStyle: {
                backgroundColor: colors.secondary,
            },
            activeLabelStyle: {
                color: colors.background,
            },
        };
    }, [colors.background, colors.body, colors.secondary]);

    return hasNativeDatePicker ? (
        <input
            type="date"
            id={testID}
            min={toISODateString(minimumDate)}
            max={toISODateString(maximumDate)}
            onChange={(e) => {
                const newDate = e.target.valueAsDate;
                if (newDate) {
                    onConfirm(newDate);
                } else {
                    onConfirm(null);
                }
            }}
            style={{
                position: "absolute",
                height: "100%",
                width: "100%",
                opacity: 0,
            }}
            ref={inputRef}
        />
    ) : (
        <View testID={testID} style={{ width: 330, maxWidth: "100%" }}>
            <DateTimePickerRoot
                mode="single"
                styles={{
                    ...defaultStyles,
                    header: normalStyle,
                    month_label: normalStyle,
                    month_selector_label: normalStyle,
                    year_selector_label: normalStyle,
                    day_label: normalStyle,
                    selected: activeStyle,
                    selected_label: activeLabelStyle,
                }}
                classNames={{
                    ...defaultClassNames,
                    day: `${defaultClassNames.day} web:hover:bg-gray-100`,
                    month: `${defaultClassNames.month} web:hover:bg-gray-100`,
                    year: `${defaultClassNames.year} web:hover:bg-gray-100`,
                }}
                components={customCalendarComponents}
                date={date || dayjs(toISODateString(maximumDate))}
                firstDayOfWeek={1}
                minDate={minimumDate}
                maxDate={maximumDate}
                locale={i18n.locale}
                timePicker={false}
                onChange={(params) => {
                    const newDate = dayjs(params.date).toDate();
                    if (date) {
                        newDate.setHours(date.getHours());
                        newDate.setMinutes(date.getMinutes());
                    }
                    onConfirm(newDate);
                    onHide?.(newDate);
                }}
            />
        </View>
    );
};
